import { useEffect } from "react";
import i18next from "i18next";
import {
  Button,
  Input,
  Field,
  RadioGroup,
  HStack,
  Stack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { observer } from "mobx-react";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import { DistanceUnit } from "@keita/lanepatrol-sdk";
import { City, CreateOrUpdateCity } from "../../../survey/models/City";
import { UpdateCitySchema } from "./UpdateCitySchema";
import { toast } from "../../../shared/views/CustomToaster";
import { AdminCityApi } from "../../services/AdminCityApi";

const formId = "updateCity";
const UpdateCityModalBase = ({
  city,
  onClose,
}: {
  onClose: () => void;
  city: City;
}) => {
  const formik = useFormik({
    initialValues: {
      name: city.name,
      country: city.country,
      lat: city.lat,
      lng: city.lng,
      distanceUnit: city.distanceUnit,
      rightHandTraffic: city.rightHandTraffic,
    },

    onSubmit: (values: CreateOrUpdateCity) => {
      AdminCityApi.update(city.id, values)
        .then(() => {
          toast("success", "Saved");
          onClose();
        })
        .catch(() => {
          toast("error", "Unable to save");
        });
    },

    validationSchema: UpdateCitySchema,
  });

  useEffect(() => {
    formik.setValues(formik.initialValues);
    // eslint-disable-next-line
  }, []);

  return (
    <CustomDialog
      header={"Edit city"}
      footer={
        <Button
          colorPalette="primary"
          type="submit"
          form={formId}
          disabled={!formik.isValid || !formik.dirty}
        >
          {`${i18next.t("shared:survey.submit.done")}`}
        </Button>
      }
      onClose={onClose}
    >
      <form id={formId} onSubmit={formik.handleSubmit}>
        <Stack gap={4}>
          <Field.Root invalid={!!formik.errors.name} required>
            <Field.Label>Name</Field.Label>
            <Input
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
            />
            <Field.ErrorText>{formik.errors.name}</Field.ErrorText>
          </Field.Root>

          <Field.Root invalid={!!formik.errors.country} required>
            <Field.Label>Country</Field.Label>
            <Input
              name="country"
              onChange={formik.handleChange}
              value={formik.values.country}
            />
            <Field.ErrorText>{formik.errors.country}</Field.ErrorText>
          </Field.Root>

          <Field.Root>
            <Field.Label>Distance unit</Field.Label>
            <RadioGroup.Root
              name="distanceUnit"
              colorPalette={"primary"}
              value={formik.values.distanceUnit}
              onValueChange={(details) => {
                formik.setFieldValue("distanceUnit", details.value);
              }}
            >
              <HStack gap="6">
                <RadioGroup.Item value={DistanceUnit.Metric}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Metric</RadioGroup.ItemText>
                </RadioGroup.Item>

                <RadioGroup.Item value={DistanceUnit.Imperial}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Imperial</RadioGroup.ItemText>
                </RadioGroup.Item>
              </HStack>
            </RadioGroup.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>Drive on</Field.Label>
            <RadioGroup.Root
              colorPalette={"primary"}
              value={formik.values.rightHandTraffic ? "right" : "left"}
              onValueChange={({ value }) => {
                formik.setFieldValue("rightHandTraffic", value === "right");
              }}
            >
              <HStack gap="6">
                <RadioGroup.Item value="left">
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Left side</RadioGroup.ItemText>
                </RadioGroup.Item>

                <RadioGroup.Item value="right">
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Right side</RadioGroup.ItemText>
                </RadioGroup.Item>
              </HStack>
            </RadioGroup.Root>
          </Field.Root>
        </Stack>
      </form>
    </CustomDialog>
  );
};

export const UpdateCityModal = observer(UpdateCityModalBase);
